import { HmmRegion } from "@eu.mdotm.api/sphere";
import { useUserValue } from "$root/functional-areas/user";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { hasAccess } from "../AuthorizationGuard";
import { typedUrlForRoute } from "../PlatformRouter/RoutesDef";
import { HierarchicalLinksModule } from "./HierarchicalLinksModule";
import type { AppDrawerModuleItem } from "./AppDrawer-shared";
import type { IconName } from "@mdotm/shadcn/legacy/components";
import { qualifier } from "$root/utils";

export const sortedHmmRegions = [HmmRegion.Global, ...Object.values(HmmRegion).filter((x) => x !== HmmRegion.Global)];

export function MarketInsightModule() {
	return <HierarchicalLinksModule className="px-4" items={useMarketInsightItems()} />;
}

export function useMarketInsightItems(): AppDrawerModuleItem[] {
	const user = useUserValue();
	const location = useLocation();
	const { t } = useTranslation();

	return [
		{
			qualifier: qualifier.appDrawer.marketInsightModules("MarketDashboard"),
			icon: "News-category-Market-view-aligment" satisfies IconName,
			enabled: hasAccess(user, { requiredService: "REPORTS" }),
			active: location.pathname.startsWith(typedUrlForRoute("MarketDashboard", {})),
			name: "Market dashboard",
			title: "Market dashboard",
			href: typedUrlForRoute("MarketDashboard", {}),
		},
		{
			qualifier: qualifier.appDrawer.marketInsightModules("Outlook"),
			icon: "Outlook" satisfies IconName,
			enabled: hasAccess(user, { requiredService: "REPORTS" }),
			active: location.pathname.startsWith(typedUrlForRoute("Outlook", {})),
			name: "Outlook",
			title: "Outlook",
			href: typedUrlForRoute("Outlook", {}),
		},
		{
			qualifier: qualifier.appDrawer.marketInsightModules("OutlookFocus"),
			icon: "assetfocus" satisfies IconName,
			enabled: hasAccess(user, { requiredService: "REPORTS" }),
			active: location.pathname.startsWith(typedUrlForRoute("AssetClass", {})),
			name: "Outlook focus",
			title: "Outlook focus",
			href: typedUrlForRoute("AssetClass", {}),
		},
		{
			qualifier: qualifier.appDrawer.marketInsightModules("RegimeAnalysisTool"),
			icon: "dashboard" satisfies IconName,
			enabled: hasAccess(user, { requiredService: "SIGNALS" }),
			active: location.pathname.startsWith(typedUrlForRoute("RegimeAnalysisTool", { region: "" })),
			name: "Regime analysis tool",
			title: "Regime analysis tool",
			href: typedUrlForRoute("RegimeAnalysisTool", {
				region: hasAccess(user, { requiredService: "GLOBAL_RISK_MAP" }) ? HmmRegion.Global : HmmRegion.Eu,
			}),
			children: sortedHmmRegions
				.filter((r) => r !== "GLOBAL" || hasAccess(user, { requiredService: "GLOBAL_RISK_MAP" }))
				.map(
					(region): AppDrawerModuleItem => ({
						qualifier: `${qualifier.appDrawer.marketInsightModules("RegimeAnalysisTool")}/${region}`,
						icon: "dashboard" satisfies IconName,
						enabled: region !== "GLOBAL" || hasAccess(user, { requiredService: "GLOBAL_RISK_MAP" }),
						active: location.pathname.startsWith(typedUrlForRoute("RegimeAnalysisTool", { region })),
						name: region,
						title: t(`GEOGRAPHY.${region}`),
						href: typedUrlForRoute("RegimeAnalysisTool", {
							region,
						}),
					}),
				),
		},
		{
			qualifier: qualifier.appDrawer.marketInsightModules("CustomReports"),
			enabled: hasAccess(user, { requiredService: "CUSTOM_REPORT_CB1" }),
			icon: "file" satisfies IconName,
			active: location.pathname.startsWith(typedUrlForRoute("CustomReports", {})),
			name: "Custom reports",
			title: "Custom reports",
			href: typedUrlForRoute("CustomReports", {}),
		},
	];
}
